// Persisted-preferences hook extracted from Sidebar.tsx during the P5 split.
// Owns: per-section open/filter flags + per-space expanded map, hydrated
// from localStorage when (workspace, user) becomes known and persisted on
// every change afterward.
//
// The `skipSidebarPersistRef` pattern is load-bearing: when the storage
// prefix flips (workspace switch or user resolution), we set state from
// localStorage and need to suppress the very next persist effect run so we
// don't write the just-loaded values back over themselves.

import { useEffect, useRef, useState } from "react";

export type SectionFilter = "all" | "recent" | "unread";

interface UseSidebarPreferencesInput {
  selectedWorkspace: string | null;
  userId: string | undefined;
}

export interface SidebarPreferences {
  channelsOpen: boolean;
  setChannelsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  channelsFilter: SectionFilter;
  setChannelsFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  spacesOpen: boolean;
  setSpacesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  spacesFilter: SectionFilter;
  setSpacesFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  dmsOpen: boolean;
  setDmsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dmsFilter: SectionFilter;
  setDmsFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  listsOpen: boolean;
  setListsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  expandedSpaces: Record<string, boolean>;
  setExpandedSpaces: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

export function useSidebarPreferences({
  selectedWorkspace,
  userId,
}: UseSidebarPreferencesInput): SidebarPreferences {
  const sidebarStoragePrefix =
    selectedWorkspace && userId
      ? `ano-sidebar:${selectedWorkspace}:${userId}`
      : null;
  const readPref = (suffix: string, fallback: string) =>
    sidebarStoragePrefix
      ? (localStorage.getItem(`${sidebarStoragePrefix}:${suffix}`) ?? fallback)
      : fallback;

  const [channelsOpen, setChannelsOpen] = useState(
    () => readPref("channels-open", "true") !== "false",
  );
  const [channelsFilter, setChannelsFilter] = useState<SectionFilter>(
    () => readPref("channels-filter", "all") as SectionFilter,
  );
  const [spacesOpen, setSpacesOpen] = useState(
    () => readPref("spaces-open", "true") !== "false",
  );
  const [spacesFilter, setSpacesFilter] = useState<SectionFilter>(
    () => readPref("spaces-filter", "all") as SectionFilter,
  );
  const [dmsOpen, setDmsOpen] = useState(
    () => readPref("dms-open", "true") !== "false",
  );
  const [dmsFilter, setDmsFilter] = useState<SectionFilter>(
    () => readPref("dms-filter", "all") as SectionFilter,
  );
  const [listsOpen, setListsOpen] = useState(
    () => readPref("lists-open", "false") !== "false",
  );
  const [expandedSpaces, setExpandedSpaces] = useState<Record<string, boolean>>(
    () => {
      try {
        return JSON.parse(readPref("expanded-spaces", "{}"));
      } catch {
        return {};
      }
    },
  );

  const skipSidebarPersistRef = useRef(false);

  // Hydrate persisted preferences when the storage prefix becomes available
  // (workspace/user may be null at mount, so lazy initializers fall back to
  // defaults). Setting state synchronously here is intentional — we're
  // syncing the React tree with an external store (localStorage) the moment
  // its key becomes known. The skipSidebarPersistRef guard then prevents the
  // companion persist effect from writing the just-loaded values back.
  useEffect(() => {
    if (!sidebarStoragePrefix) return;
    const r = (suffix: string, fallback: string) =>
      localStorage.getItem(`${sidebarStoragePrefix}:${suffix}`) ?? fallback;
    skipSidebarPersistRef.current = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChannelsOpen(r("channels-open", "true") !== "false");
    setChannelsFilter(r("channels-filter", "all") as SectionFilter);
    setSpacesOpen(r("spaces-open", "true") !== "false");
    setSpacesFilter(r("spaces-filter", "all") as SectionFilter);
    setDmsOpen(r("dms-open", "true") !== "false");
    setDmsFilter(r("dms-filter", "all") as SectionFilter);
    setListsOpen(r("lists-open", "false") !== "false");
    try {
      setExpandedSpaces(JSON.parse(r("expanded-spaces", "{}")));
    } catch {
      setExpandedSpaces({});
    }
  }, [sidebarStoragePrefix]);

  useEffect(() => {
    if (!sidebarStoragePrefix) return;
    if (skipSidebarPersistRef.current) {
      skipSidebarPersistRef.current = false;
      return;
    }
    localStorage.setItem(
      `${sidebarStoragePrefix}:channels-open`,
      String(channelsOpen),
    );
    localStorage.setItem(
      `${sidebarStoragePrefix}:channels-filter`,
      channelsFilter,
    );
    localStorage.setItem(
      `${sidebarStoragePrefix}:spaces-open`,
      String(spacesOpen),
    );
    localStorage.setItem(`${sidebarStoragePrefix}:spaces-filter`, spacesFilter);
    localStorage.setItem(`${sidebarStoragePrefix}:dms-open`, String(dmsOpen));
    localStorage.setItem(`${sidebarStoragePrefix}:dms-filter`, dmsFilter);
    localStorage.setItem(
      `${sidebarStoragePrefix}:lists-open`,
      String(listsOpen),
    );
    localStorage.setItem(
      `${sidebarStoragePrefix}:expanded-spaces`,
      JSON.stringify(expandedSpaces),
    );
  }, [
    channelsOpen,
    channelsFilter,
    spacesOpen,
    spacesFilter,
    dmsOpen,
    dmsFilter,
    listsOpen,
    expandedSpaces,
    sidebarStoragePrefix,
  ]);

  return {
    channelsOpen,
    setChannelsOpen,
    channelsFilter,
    setChannelsFilter,
    spacesOpen,
    setSpacesOpen,
    spacesFilter,
    setSpacesFilter,
    dmsOpen,
    setDmsOpen,
    dmsFilter,
    setDmsFilter,
    listsOpen,
    setListsOpen,
    expandedSpaces,
    setExpandedSpaces,
  };
}
